import React from 'react';
import { Skeleton } from 'src/components/ui/skeleton';

interface MetricCardProps {
  title: string;
  currentValue: number | null;
  difference: number | null;
  isLoading: boolean;
  percentage?: boolean;
  dollarValue?: boolean;
  lowerIsBetter?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, currentValue, difference, isLoading, percentage, dollarValue, lowerIsBetter }) => {
  const formatNumber = (num: number | null = 0, decimals: boolean | null = false): string => {
    if (num === null) return 'N/A';
    if (decimals) {
      return num.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } else {
      return num.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  };

  const calculatePercentageChange = (current: number | null = 0, diff: number | null = 0): string => {
    const currentVal = current ?? 0;
    const diffVal = diff ?? 0;
    const change = (diffVal / currentVal) * 100;
    if (isNaN(change)) return '0';
    if (!isFinite(change)) return '100';
    const decimalPlaces = Math.abs(change) >= 100 ? 0 : 1;
    return Math.abs(change).toFixed(decimalPlaces).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const percentageChange = calculatePercentageChange(currentValue, difference);
  const isImproving = difference !== null && difference > 0;

  return (
    <div className="rounded-lg bg-white border border-gray-400 p-4 flex-1 min-w-[190px]">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-600 mr-2">
          {title}
        </h3>
        <p className={`flex items-center overflow-hidden whitespace-nowrap text-sm font-medium ${lowerIsBetter ? (isImproving ? 'text-[#e65051]' : 'text-[#1ebd87]') : (isImproving ? 'text-[#1ebd87]' : 'text-[#e65051]')}`}>
          {percentageChange !== '0' && (
            <>
              {isImproving ? (
                <span className="font-bold text-lg mt-[4px]">
                  ↑
                </span>
              ) : (
                <span className="font-bold text-lg mt-[4px]">
                  ↓
                </span>
              )}

              <span className={"ml-1 truncate"}>
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                {percentageChange}%
              </span>
            </>
          )}
        </p>
      </div>
      {isLoading ? (
        <Skeleton className="w-full h-6" />
      ) : (
        <>
          <p className="font-bold text-xl">
            {dollarValue && '$'}
            {formatNumber(currentValue, dollarValue || percentage)}
            {percentage && '%'}
          </p>
        </>
      )}
    </div>
  );
};

export default MetricCard;